<template>
  <div class="relative min-h-[400px]">
    <div class="w-full flex flex-wrap items-center justify-between my-8 gap-4">
      <div class="flex-1 min-w-[200px] md:min-w-[280px] order-1">
        <SearchInput
          v-model="textInput"
          @input="onSearchByTabIndex"
          @search-click="handleSearchIconClick"
          width="100%"
          :loading="loading"
          :label="`Buscar...`"
          hide-details
          density="compact"
        />
      </div>

      <div class="relative flex-shrink-0 order-2 lg:order-3" @click.stop>
        <OutlineButton
          text="Exportar"
          @click="showExportMenu = !showExportMenu"
          height="32px"
          width="124px"
          :prepend-icon="mdiChevronDown"
          :disabled="isLoading"
        />

        <div
          v-if="showExportMenu"
          class="export-wrapper absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg border border-black/5 focus:outline-none z-20"
        >
          <div>
            <a
              @click="handleExport()"
              class="flex cursor-pointer items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-100"
            >
              <excelIcon width="20" height="20" />
              <span>Exportar para Excel</span>
            </a>
          </div>
        </div>
      </div>

      <button
        @click="showMobileFilters = !showMobileFilters"
        class="flex md:hidden items-center gap-1 px-3 h-[36px] border border-gray-300 rounded-full text-gray-500 text-sm order-3"
      >
        <v-icon :icon="mdiFilterVariant" :size="18" />
        Filtros
        <v-icon :icon="showMobileFilters ? mdiChevronUp : mdiChevronDown" :size="16" />
      </button>

      <div
        :class="[
          'w-full flex flex-wrap gap-2 justify-start order-3 lg:w-auto lg:order-2',
          showMobileFilters ? '' : 'hidden md:flex'
        ]"
      >
        <div @click.stop>
          <DateFilter
            :is-open="activeFilter === 'date'"
            @click="toggleFilter('date')"
            @select="handleDateFilter"
          />
        </div>

        <div @click.stop>
          <TimeFilter
            label="Horário"
            :icon="clockIcon"
            :options="timeOptions"
            :initial-start-value="initialHourStart"
            :initial-end-value="initialHourEnd"
            :is-open="activeFilter === 'time'"
            @apply="handleHourFilter"
            @toggle="toggleFilter('time')"
          />
        </div>

        <div class="relative" @click.stop="toggleFilter('schedule')">
          <SelectFilter
            label="Escala"
            :icon="scaleIcon"
            :options="scheduleOptions"
            :is-open="activeFilter === 'schedule'"
            searchable
            dropdown-width="w-64"
            @select="handleScheduleSelect"
          />
        </div>

        <div class="relative" @click.stop="toggleFilter('healthUnit')">
          <SelectFilter
            label="Unidade"
            :icon="hospitalIcon"
            :options="healthUnitOptions"
            :is-open="activeFilter === 'healthUnit'"
            searchable
            dropdown-width="w-64"
            @select="handleHealthUnitSelect"
          />
        </div>

        <div class="relative" @click.stop="toggleFilter('position')">
          <SelectFilter
            label="Posições"
            :icon="positionIcon"
            :options="positionsOptions"
            :is-open="activeFilter === 'position'"
            searchable
            dropdown-width="w-64"
            @select="handlePositionSelect"
          />
        </div>

        <div class="relative" @click.stop="toggleFilter('operationalGroup')">
          <SelectFilter
            label="Grupo Operacional"
            :icon="accountGroupIcon"
            :options="operationalGroupOptions"
            :is-open="activeFilter === 'operationalGroup'"
            searchable
            dropdown-width="w-64"
            @select="handleOperationalGroupSelect"
          />
        </div>
      </div>
    </div>

    <div v-if="hasActiveFilters" class="my-4 flex flex-wrap gap-2 items-center">
      <span
        v-if="selectedDateLabel"
        class="inline-flex items-center rounded-full border border-primary30 px-3 py-2 md:px-5 md:py-3 text-xs md:text-sm text-primary30 bg-primary-50"
      >
        {{ selectedDateLabel }}
        <button
          @click="clearDate"
          class="ml-2 text-primary-500 hover:text-primary-700 font-bold focus:outline-none"
        >
          x
        </button>
      </span>

      <span
        v-if="selectedScheduleLabel"
        class="inline-flex items-center rounded-full border border-primary30 px-3 py-2 md:px-5 md:py-3 text-xs md:text-sm text-primary30 bg-primary-50"
      >
        {{ selectedScheduleLabel }}
        <button
          @click="clearSchedule"
          class="ml-2 text-primary-500 hover:text-primary-700 font-bold focus:outline-none"
        >
          x
        </button>
      </span>

      <span
        v-if="selectedHealthUnitLabel"
        class="inline-flex items-center rounded-full border border-primary30 px-3 py-2 md:px-5 md:py-3 text-xs md:text-sm text-primary30 bg-primary-50"
      >
        {{ selectedHealthUnitLabel }}
        <button
          @click="clearHealthUnit"
          class="ml-2 text-primary-500 hover:text-primary-700 font-bold focus:outline-none"
        >
          x
        </button>
      </span>

      <span
        v-if="selectedTimeLabel"
        class="inline-flex items-center rounded-full border border-primary30 px-3 py-2 md:px-5 md:py-3 text-xs md:text-sm text-primary30 bg-primary-50"
      >
        {{ selectedTimeLabel }}
        <button
          @click="clearTimeFilter"
          class="ml-2 text-primary-500 hover:text-primary-700 font-bold focus:outline-none"
        >
          x
        </button>
      </span>

      <span
        v-if="selectedPositionLabel"
        class="inline-flex items-center rounded-full border border-primary30 px-3 py-2 md:px-5 md:py-3 text-xs md:text-sm text-primary30 bg-primary-50"
      >
        {{ selectedPositionLabel }}
        <button
          @click="clearPosition"
          class="ml-2 text-primary-500 hover:text-primary-700 font-bold focus:outline-none"
        >
          x
        </button>
      </span>

      <span
        v-if="selectedOperationalGroup"
        class="inline-flex items-center rounded-full border border-primary30 px-3 py-2 md:px-5 md:py-3 text-xs md:text-sm text-primary30 bg-primary-50"
      >
        {{ selectedOperationalGroup }}
        <button
          @click="clearOperationalGroup"
          class="ml-2 text-primary-500 hover:text-primary-700 font-bold focus:outline-none"
        >
          x
        </button>
      </span>

      <div class="ml-2 flex">
        <button
          @click="clearAllFilters"
          class="text-sm font-bold text-primary hover:text-primary-darken-1 transition-colors"
        >
          Limpar filtros
        </button>
      </div>
    </div>

    <div class="flex flex-col gap-y-4">
      <template v-if="isLoading">
        <HistoryShiftCardSkeleton v-for="n in itemsPerPage" :key="`skeleton-${n}`" />
      </template>

      <template v-else>
        <EmptyList
          v-if="!historyData.length"
          title="Nenhum histórico de troca de plantões encontrado"
          class="mt-8"
        />

        <template v-else>
          <HistoryShiftCard
            v-for="item in historyData"
            :key="item.id"
            :history-item="item"
            @view-shift="handleViewShift"
            @view-terms="handleViewTerms(item)"
          />

          <div v-if="pageCount > 1" class="mt-8 flex justify-center">
            <v-pagination
              v-model="currentPage"
              :length="pageCount"
              :total-visible="4"
              density="comfortable"
              color="primary"
              @update:model-value="handlePageChange"
            />
          </div>
        </template>
      </template>
    </div>
  </div>

  <StationDetailsModal
    v-if="stationSelected"
    :infos="stationSelected"
    :show-modal="showDetailsModal"
    @update:showModal="showDetailsModal = $event"
    @close-modal="showDetailsModal = false"
    @edit-station="handleEditStation"
  />

  <UpdateStationModal
    v-if="stationSelected && showUpdateModal"
    :infos="stationSelected"
    :exception-status="stationSelected.exceptionStatus"
    :show-modal="showUpdateModal"
    @close-modal="handleCloseUpdateModal"
    @go-back="handleGoBackFromUpdate"
    @saved="handleUpdateSaved"
  />

  <TermsOfUseModal
    :is-open="isTermsModalOpen"
    :history-item="selectedHistoryItemForTerms"
    @update:is-open="isTermsModalOpen = $event"
  />
</template>

<script setup lang="ts">
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { ref, onMounted, onBeforeUnmount, computed, defineAsyncComponent, watch } from 'vue'
import HistoryShiftCard from './HistoryShiftCard.vue'
import EmptyList from '@/components/EmptyList.vue'
import type { HandoverInviteResponse } from '@/Network/Types/Responses/HandoverInviteResponses'
import HandoverInviteService, { HandoverInviteStatus } from '@/Network/Services/HandoverInvites'
import {
  StationToUpdate,
  mapCoverageToStationUpdate
} from '../Dashboard/modals/createStation/helpers'
import UpdateStationModal from '../Dashboard/modals/updateStation/update.station.modal.vue'
import { useDoctorStore } from '@/pinia-store/useDoctorStore'
import { CompanyDoctorInviteStatusEnum } from '@/Network/Types/Requests/CompanyRequests'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import DateFilter from '@/components/Filters/DateFilter.vue'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import OutlineButton from '../../../components/designSystem/Buttons/OutlineButton/index.vue'
import { mdiChevronDown, mdiChevronUp, mdiFilterVariant } from '@mdi/js'
import { format } from 'date-fns'
import TimeFilter from '@/components/Filters/TimeFilter.vue'
import { timeOptions } from '@/utils/timeOptions'
import { ListHandoverHistoryParams } from '@/Network/Types/Requests/HandoverInviteRequests'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import { storeToRefs } from 'pinia'
import Network from '@/Network'
import { useToast } from 'vue-toastification'
import Axios from 'axios'
import TermsOfUseModal from './modals/terms.of.use.modal.vue'
import StationDetailsModal from '../Dashboard/components/stationDetailsModal.vue'
import FieldOptionsService from '@/Network/Services/FieldOptionsService'
import HistoryShiftCardSkeleton from '@/components/designSystem/Skeleton/HistoryShiftCardSkeleton.vue'

const scaleIcon = defineAsyncComponent(() => import('@/assets/icons/scale.vue'))
const hospitalIcon = defineAsyncComponent(() => import('@/assets/icons/hospital.vue'))
const positionIcon = defineAsyncComponent(() => import('@/assets/icons/position.vue'))
const clockIcon = defineAsyncComponent(() => import('@/assets/icons/clock.vue'))
const excelIcon = defineAsyncComponent(() => import('@/assets/icons/excel.vue'))
const accountGroupIcon = defineAsyncComponent(() => import('@/assets/icons/AccountGroupIcon.vue'))

const isDashboardV2 = useFeatureFlags().flags.enabledDashboardV2

interface DateRange {
  start: Date | null
  end: Date | null
}

const props = defineProps<{
  isActive: boolean
}>()

watch(
  () => props.isActive,
  (newIsActiveValue) => {
    if (!newIsActiveValue) {
      activeFilter.value = null
    }
  }
)

let searchDebounceTimer: number
const toast = useToast()
const textInput = ref('')
const selectedStartDate = ref<string | null>(null)
const selectedEndDate = ref<string | null>(null)
const loading = ref(false)

const itemsPerPage = 6
const currentPage = ref(1)
const totalHistoryItems = ref(0)

const historyData = ref<HandoverInviteResponse[]>([])
const isLoading = ref(true)
const error = ref<string | null>(null)

const handoverService = new HandoverInviteService()
const pageCount = computed(() => {
  return Math.ceil(totalHistoryItems.value / itemsPerPage)
})

const showMobileFilters = ref(false)
const showExportMenu = ref(false)
const showDetailsModal = ref(false)
const showUpdateModal = ref(false)

const stationSelected = ref<StationToUpdate | null>(null)
const selectedHealthUnitLabel = ref<string | null>(null)
const selectedDateLabel = ref<string | null>(null)
const selectedScheduleLabel = ref<string | null>(null)
const selectedTimeLabel = ref<string | null>(null)
const selectedPositionLabel = ref<string | null>(null)
const initialHourStart = ref('00:00')
const initialHourEnd = ref('23:45')
const activeFilter = ref<string | null>(null)
const selectedScheduleId = ref<string | null>(null)
const selectedHealthUnitId = ref<string | null>(null)
const selectedPositionId = ref<string | null>(null)
const operationalGroupOptions = ref<{ label: string; value: string }[]>([])
const selectedOperationalGroup = ref<string | null>(null)

const isTermsModalOpen = ref(false)
const selectedHistoryItemForTerms = ref<HandoverInviteResponse | null>(null)

const doctorStore = useDoctorStore()
const healthUnitStore = useHealthUnitStore()
const scheduleStore = useScheduleStore()

const { listHealthUnits } = storeToRefs(healthUnitStore)
const { listSchedules } = storeToRefs(scheduleStore)

const healthUnitOptions = computed(() =>
  listHealthUnits.value.map((unit) => ({
    label: unit.title,
    value: String(unit.value)
  }))
)

const scheduleOptions = computed(() =>
  listSchedules.value.map((schedule) => ({
    label: schedule.title,
    value: String(schedule.value)
  }))
)

const positionsData = ref<any[]>([])
const positionsOptions = computed(() =>
  positionsData.value.map((pos) => ({
    label: pos.name,
    value: String(pos.id)
  }))
)

async function fetchHistoryData(page: number) {
  isLoading.value = true
  error.value = null

  try {
    const offset = (page - 1) * itemsPerPage

    const params: ListHandoverHistoryParams = {
      limit: itemsPerPage,
      offset: offset,
      status: [HandoverInviteStatus.ACCEPTED]
    }
    if (textInput.value) {
      params.query = textInput.value
    }
    if (selectedStartDate.value) {
      params.startDate = selectedStartDate.value
    }
    if (selectedEndDate.value) {
      params.endDate = selectedEndDate.value
    }
    if (selectedTimeLabel.value) {
      params.startTime = initialHourStart.value
      params.endTime = initialHourEnd.value
    }

    if (selectedScheduleLabel.value) {
      params.scheduleName = selectedScheduleLabel.value
    }
    if (selectedHealthUnitLabel.value) {
      params.unitName = selectedHealthUnitLabel.value
    }
    if (selectedPositionLabel.value) {
      params.positionName = selectedPositionLabel.value
    }
    if (selectedOperationalGroup.value) {
      params.operationalGroups = [selectedOperationalGroup.value]
    }

    const response = await handoverService.listHistory(params)

    historyData.value = response.data
    totalHistoryItems.value = response.count
    currentPage.value = page
  } catch (err) {
    console.error('Falha ao buscar dados da API:', err)
    error.value = 'Ocorreu um erro ao carregar o histórico. Tente novamente mais tarde.'
  } finally {
    isLoading.value = false
  }
}

// eslint-disable-next-line complexity
function handleViewShift(handoverId: number | string) {
  const item = historyData.value.find((h) => h.id === handoverId)
  if (!item || !item.doctorStation || !item.doctorStation.station) {
    console.error('Dados do plantão não encontrados para o ID:', handoverId)
    return
  }

  const stationForModal: StationToUpdate = {
    stationId: item.doctorStation.station.id,
    companyId: item.doctorStation.station.schedule?.healthUnit?.company?.id ?? 0,
    doctorStationId: item.doctorStation.id,
    date: new Date(item.doctorStation.date),
    startHour: new Date(item.doctorStation.startHour),
    endHour: new Date(item.doctorStation.endHour),
    timezone: item.doctorStation.station.timezone ?? '-03:00',
    stationType: item.doctorStation.station.type ?? 'N/A',
    ourTeam: item.doctorStation.station.ourTeam ?? false,
    value: item.doctorStation.value ?? 0,
    stationValue: item.doctorStation.station.value ?? 0,
    inCash: item.doctorStation.inCash ?? false,
    advanceRequested: item.doctorStation.requestAntecipe ?? false,
    advancePaid: item.doctorStation.antecipeWasPaid ?? false,
    modifiedValue: item.doctorStation.modifiedValue ?? false,
    position: item.doctorStation.station.position,
    checks: [],
    healthUnitLatitude: '',
    healthUnitLongitude: '',
    doctorId: undefined,
    doctorPending: undefined,
    urlConviteDoctor: undefined,
    userSponsor: item.doctorStation?.userSponsor,
    scheduleId: item.doctorStation?.station?.schedule?.id
  }

  stationSelected.value = stationForModal
  showDetailsModal.value = true
}

function handleViewTerms(item: HandoverInviteResponse) {
  selectedHistoryItemForTerms.value = item
  isTermsModalOpen.value = true
}

async function handleEditStation(fullStationData: any) {
  if (!stationSelected.value) return

  const exceptionStatus = fullStationData.doctorStation?.exceptionSummaryStatus || null

  stationSelected.value = {
    ...stationSelected.value,

    stationId: fullStationData.id,
    companyId: fullStationData.schedule.healthUnit.company.id,
    doctorId: fullStationData?.doctorStation?.doctor?.id || undefined,
    date: fullStationData.date,
    startHour: fullStationData.startHour,
    endHour: fullStationData.endHour,
    timezone: fullStationData.timezone,
    stationType: fullStationData.doctorStation.type,
    ourTeam: fullStationData.doctorStation.ourTeam,
    advanceRequested: fullStationData.doctorStation.requestAntecipe,
    advancePaid: fullStationData.doctorStation.antecipeWasPaid,
    modifiedValue: fullStationData.doctorStation.value,
    value: fullStationData.doctorStation.value,
    checks: fullStationData.doctorStation.doctorChecks,
    stationValue: fullStationData.value,
    position: fullStationData.position,
    doctorStationId: fullStationData.doctorStation.id,
    schedule: fullStationData.schedule,
    doctorStation: fullStationData.doctorStation,
    shift: fullStationData.shift,
    exceptionStatus: exceptionStatus,
    // H6: setor/especialidade do detalhe fresco (A9); o modal só escreve o que mudar.
    // A11: só com a flag ligada (desligada, o objeto é o da base).
    ...(isDashboardV2
      ? { sector: fullStationData.sector ?? null, specialty: fullStationData.specialty ?? null }
      : {}),
    // E-06: estado da cobertura do detalhe fresco — libera a edição sem médico.
    ...mapCoverageToStationUpdate(fullStationData)
  }

  await doctorStore.applyFilters(
    {
      search: '',
      names: [],
      crms: [],
      crmUFs: [],
      companies: [String(stationSelected.value.companyId)],
      inviteStatus: [CompanyDoctorInviteStatusEnum.ACCEPTED, CompanyDoctorInviteStatusEnum.PENDING]
    },
    false
  )

  showDetailsModal.value = false
  showUpdateModal.value = true
}

const onSearchByTabIndex = () => {
  clearTimeout(searchDebounceTimer)
  searchDebounceTimer = window.setTimeout(() => {
    fetchHistoryData(1)
  }, 500)
}

function handleClickOutside() {
  activeFilter.value = null
  showExportMenu.value = false
}

function toggleFilter(filterName: string) {
  if (activeFilter.value === filterName) {
    activeFilter.value = null
  } else {
    activeFilter.value = filterName
  }
}

const hasActiveFilters = computed(() => {
  const hasSearch = textInput.value !== ''
  const hasDate = selectedDateLabel.value !== null
  const hasTime = selectedTimeLabel.value !== null
  const hasSchedule = selectedScheduleLabel.value !== null
  const hasUnit = selectedHealthUnitLabel.value !== null
  const hasPosition = selectedPositionLabel.value !== null
  const hasGroup = selectedOperationalGroup.value !== null

  return hasSearch || hasDate || hasTime || hasSchedule || hasUnit || hasPosition || hasGroup
})

function clearAllFilters() {
  textInput.value = ''
  selectedDateLabel.value = null
  selectedStartDate.value = null
  selectedEndDate.value = null
  selectedScheduleLabel.value = null
  selectedScheduleId.value = null
  selectedHealthUnitLabel.value = null
  selectedHealthUnitId.value = null
  selectedPositionLabel.value = null
  selectedPositionId.value = null
  selectedTimeLabel.value = null
  initialHourStart.value = '00:00'
  initialHourEnd.value = '23:45'
  selectedOperationalGroup.value = null
  fetchHistoryData(1)
}

function handleCloseUpdateModal() {
  showUpdateModal.value = false
  fetchHistoryData(currentPage.value)
}

function handleGoBackFromUpdate() {
  showUpdateModal.value = false
  showDetailsModal.value = true
}

function handleUpdateSaved() {
  fetchHistoryData(currentPage.value)
  handleGoBackFromUpdate()
}

async function handlePageChange(newPage: number) {
  await fetchHistoryData(newPage)
}

const handleExport = async (): Promise<void> => {
  if (!selectedStartDate.value || !selectedEndDate.value) {
    toast.warning('Selecione um período antes de exportar')
    return
  }

  try {
    const filename = `relatorio-de-trocas-de-plantoes-${selectedStartDate.value}-${selectedEndDate.value}.xlsx`

    const link = await Network.handoverInvite.exportAcceptedHandoversXlsx({
      query: textInput.value?.trim(),
      startDate: selectedStartDate.value,
      endDate: selectedEndDate.value,
      startTime: initialHourStart.value,
      endTime: initialHourEnd.value,
      status: [HandoverInviteStatus.ACCEPTED],
      filename
    })

    document.body.appendChild(link)
    link.click()
    toast.success('Relatório exportado com sucesso')
  } catch (error) {
    if (Axios.isAxiosError(error) && error.response?.status === 404) {
      toast.warning('Nenhum apontamento encontrado dentro do período especificado')
      return
    }
    toast.error('Erro ao exportar relatório de trocas, contate um administrador!')
  } finally {
    showExportMenu.value = false
  }
}

function handleDateFilter({ start, end }: DateRange) {
  if (start && end) {
    selectedStartDate.value = format(start, 'yyyy-MM-dd')
    selectedEndDate.value = format(end, 'yyyy-MM-dd')
    selectedDateLabel.value = `Período: ${format(start, 'dd/MM/yyyy')} a ${format(end, 'dd/MM/yyyy')}`
  }
  fetchHistoryData(1)
}

function handleScheduleSelect(value: string) {
  const selected = scheduleOptions.value.find((opt) => opt.value === value)
  if (selected) {
    selectedScheduleLabel.value = selected.label
    selectedScheduleId.value = selected.value
    fetchHistoryData(1)
  }
  activeFilter.value = null
}

function handleHealthUnitSelect(value: string) {
  const selected = healthUnitOptions.value.find((opt) => opt.value === value)
  if (selected) {
    selectedHealthUnitLabel.value = selected.label
    selectedHealthUnitId.value = selected.value
    fetchHistoryData(1)
  }
  activeFilter.value = null
}

function handlePositionSelect(value: string) {
  const selected = positionsOptions.value.find((opt) => opt.value === value)
  if (selected) {
    selectedPositionLabel.value = selected.label
    selectedPositionId.value = selected.value
    fetchHistoryData(1)
  }
  activeFilter.value = null
}

function handleHourFilter(payload: { start: string; end: string }) {
  initialHourStart.value = payload.start
  initialHourEnd.value = payload.end
  selectedTimeLabel.value = `Horário: ${payload.start} - ${payload.end}`
  activeFilter.value = null
  fetchHistoryData(1)
}

function clearDate() {
  selectedDateLabel.value = null
  selectedStartDate.value = null
  selectedEndDate.value = null

  fetchHistoryData(1)
}

function clearSchedule() {
  selectedScheduleLabel.value = null
  selectedScheduleId.value = null
  fetchHistoryData(1)
}

function clearHealthUnit() {
  selectedHealthUnitLabel.value = null
  selectedHealthUnitId.value = null
  fetchHistoryData(1)
}

function clearPosition() {
  selectedPositionLabel.value = null
  selectedPositionId.value = null
  fetchHistoryData(1)
}

function clearTimeFilter() {
  selectedTimeLabel.value = null
  initialHourStart.value = '00:00'
  initialHourEnd.value = '23:45'

  fetchHistoryData(1)
}

function handleSearchIconClick() {
  clearTimeout(searchDebounceTimer)
  fetchHistoryData(1)
}

function handleOperationalGroupSelect(value: string) {
  selectedOperationalGroup.value = value
  fetchHistoryData(1)
  activeFilter.value = null
}

function clearOperationalGroup() {
  selectedOperationalGroup.value = null
  fetchHistoryData(1)
}

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

onMounted(async () => {
  document.addEventListener('click', handleClickOutside)
  try {
    const fieldOptionsService = new FieldOptionsService()
    const [positionsRes, operationalGroupsRes] = await Promise.all([
      Network.position.get(),
      fieldOptionsService.getOperationalGroups(),
      healthUnitStore.fetchAllHealthUnitsForSelect(),
      scheduleStore.getAllSchedules(),
      fetchHistoryData(1)
    ])

    positionsData.value = positionsRes
    operationalGroupOptions.value = operationalGroupsRes.map((group) => ({
      label: group.name,
      value: group.name
    }))
  } catch (error) {
    console.error('Erro ao carregar dados iniciais da página:', error)
  }
})
</script>
<style scoped>
@media (min-width: 1197px) and (max-width: 1336px) {
  .export-wrapper {
    display: flex;
    position: relative;
    left: -20px;
    z-index: 9999;
  }
}
@media (min-width: 1024px) and (max-width: 1040px) {
  .export-wrapper {
    display: flex;
    position: relative;
    left: -20px;
    z-index: 9999;
  }
}
</style>
